import { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { BookOpen, Search, Plus, Lock } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import PageHeader from '@/components/PageHeader'
import AccentCard from '@/components/AccentCard'
import { wikiAPI } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { can } from '@/lib/permissions'

const CATEGORY_LABELS = {
  // Deelnemer
  'beginnen':          'Beginnen',
  'toernooien':        'Toernooien',
  'compo':             'Compo\'s',
  'punten-prijzen':    'Punten & prijzen',
  'bar-kassa':         'Bar & kassa',
  'teams':             'Teams',
  'hulp':              'Hulp',
  // Admin
  'lan-organisatie':   'LAN-organisatie',
  'toernooi-admin':    'Toernooi-beheer',
  'hardware-midi':     'Hardware & MIDI',
  'obs-streaming':     'OBS & streaming',
  'servers-infra':     'Servers & infra',
  'homelab':           'Homelab',
  // Fallbacks voor legacy artikelen
  'tournament-formats': 'Toernooien',
  'admin-flows':        'Beheer',
  'compo-formats':      'Compo\'s',
  'lan-guides':         'LAN-guides',
  'general':            'Algemeen',
  'network':            'Homelab',
  'ops':                'Servers & infra',
}
const CATEGORY_ORDER = [
  'beginnen', 'toernooien', 'compo', 'punten-prijzen', 'bar-kassa', 'teams', 'hulp',
  'lan-organisatie', 'toernooi-admin', 'hardware-midi', 'obs-streaming', 'servers-infra', 'homelab',
]
function catLabel(k) { return CATEGORY_LABELS[k] || k }

export default function WikiPage() {
  const { user }           = useAuth()
  const [articles, set]    = useState([])
  const [q, setQ]          = useState('')
  const [category, setCat] = useState('all')
  const [loading, setLoad] = useState(true)
  const [error, setErr]    = useState(null)

  const canEdit = can(user?.role, 'manage_users')

  useEffect(() => {
    wikiAPI.list()
      .then(r => set(r.data?.articles || []))
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoad(false))
  }, [])

  const categories = useMemo(() => {
    const s = new Set(articles.map(a => a.category))
    return ['all', ...Array.from(s).sort()]
  }, [articles])

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase()
    return articles.filter(a => {
      if (category !== 'all' && a.category !== category) return false
      if (!query) return true
      return (
        (a.title || '').toLowerCase().includes(query) ||
        (a.summary || '').toLowerCase().includes(query)
      )
    })
  }, [articles, q, category])

  // Groepeer per categorie voor de weergave, in vaste volgorde
  // (deelnemer-cats eerst, admin-cats daarna). Onbekende categorieën
  // vallen achteraan op alfabet.
  const grouped = useMemo(() => {
    const g = {}
    filtered.forEach(a => {
      (g[a.category] ||= []).push(a)
    })
    const ordered = {}
    for (const k of CATEGORY_ORDER) if (g[k]) ordered[k] = g[k]
    for (const k of Object.keys(g).sort()) if (!ordered[k]) ordered[k] = g[k]
    return ordered
  }, [filtered])

  return (
    <div>
      <PageHeader
        icon={BookOpen}
        title="Wiki"
        subtitle="Info verzamelen: bracket-formats, admin-flows, uitleg"
        actions={canEdit ? (
          <Button asChild size="sm">
            <Link to="/wiki/nieuw"><Plus className="h-4 w-4 mr-1" />Nieuw artikel</Link>
          </Button>
        ) : null}
      />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={e => setQ(e.target.value)}
                 placeholder="Zoek artikel…" className="pl-9" />
        </div>
        <div className="flex flex-wrap gap-1">
          {categories.map(c => (
            <button key={c}
                    onClick={() => setCat(c)}
                    className={`px-3 py-1 text-xs font-mono uppercase tracking-widest rounded border transition-colors ${
                      category === c
                        ? 'bg-acid text-background border-acid'
                        : 'border-border text-muted-foreground hover:text-foreground hover:border-acid/40'
                    }`}>
              {c === 'all' ? 'Alles' : catLabel(c)}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Laden…</p>}
      {error   && <p className="text-sm text-signal">{error}</p>}

      {!loading && filtered.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            {q ? `Niets gevonden voor "${q}".` : 'Nog geen artikelen.'}
          </CardContent>
        </Card>
      )}

      {Object.entries(grouped).map(([cat, list]) => (
        <section key={cat} className="mb-6">
          <div className="eyebrow mb-2">{catLabel(cat)}</div>
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {list.map(a => <ArticleCard key={a.id} article={a} />)}
          </div>
        </section>
      ))}
    </div>
  )
}

function ArticleCard({ article }) {
  const isPlaceholder = /nog niet ondersteund/i.test(article.summary || '')
  const isAdminOnly = article.visibility === 'admin'
  return (
    <Link to={`/wiki/${article.slug}`} className="block no-underline">
      <AccentCard tone={isAdminOnly ? 'signal' : (isPlaceholder ? 'muted' : 'acid')}
                  className="transition-colors hover:border-acid h-full">
        <CardContent className="p-3">
          <div className="flex items-start gap-2">
            <div className="font-semibold text-sm flex-1">{article.title}</div>
            {isAdminOnly && (
              <span title="Alleen zichtbaar voor admin"
                    className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-signal shrink-0">
                <Lock className="h-2.5 w-2.5" /> admin
              </span>
            )}
          </div>
          {article.summary && (
            <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
              {article.summary}
            </div>
          )}
        </CardContent>
      </AccentCard>
    </Link>
  )
}
